/**
 * AGM RSVP DEV-ONLY harness: a fake `/v2/agm`.
 *
 * This lets every screen the page can reach (A open, B answered, the last-3-days and
 * last-day deadlines, C closed, ended, D no AGM, E not eligible, F could not load)
 * be clicked through on web, a simulator or an emulator, and the next save be made
 * to succeed, close (409) or fail, without an EM member on the AGM database or a
 * CMS entry. `agm-api.js` routes each call here while the harness is on.
 *
 * Dates are built from the clock at each call, so "closes in 2 days" and "closes
 * tonight" stay true however long the harness has been left switched on.
 *
 * Inert unless switched on from /agm-states (localStorage), and never active in a
 * production build. Mirrors src/utils/bf-pledge-harness.js. All data here is
 * placeholder data: no member, no answer, no real address.
 */

import { AGM_ENQUIRIES_EMAIL } from '@/constants/agm';

const AGM_HARNESS_KEY = 'hogc_agm_harness';

const HOUR_MS = 60 * 60 * 1000;
const SGT_OFFSET_MS = 8 * HOUR_MS;

// ── Fixture bodies (placeholder data) ───────────────────────────────────────

const VENUE = 'Heart of God Church, Level 4, Auditorium';
const BABYSPACE_URL = 'https://hogc.link/agmbabyspace';
const BRIEFING = Object.freeze({
  title: 'EM briefing',
  videoUrl: 'https://player.vimeo.com/video/76979871',
});

// 2:00pm Singapore, `days` from today (Singapore), for two hours.
function eventAt(now, days) {
  const sgt = new Date(now.getTime() + SGT_OFFSET_MS);
  const startsAt = new Date(
    Date.UTC(sgt.getUTCFullYear(), sgt.getUTCMonth(), sgt.getUTCDate() + days, 6, 0, 0),
  );
  const endsAt = new Date(startsAt.getTime() + 2 * HOUR_MS);
  const year = new Date(startsAt.getTime() + SGT_OFFSET_MS).getUTCFullYear();
  return {
    name: `Annual General Meeting ${year}`,
    venue: VENUE,
    startsAt: startsAt.toISOString(),
    endsAt: endsAt.toISOString(),
  };
}

// 11:59pm Singapore, `days` from today (Singapore).
function deadlineAt(now, days) {
  const sgt = new Date(now.getTime() + SGT_OFFSET_MS);
  return new Date(
    Date.UTC(sgt.getUTCFullYear(), sgt.getUTCMonth(), sgt.getUTCDate() + days, 15, 59, 0),
  );
}

const eligibleBody = (now, { eventDays = 20, closesInDays = 14, ...overrides } = {}) => ({
  eligible: true,
  agmActive: true,
  event: eventAt(now, eventDays),
  rsvp: { attending: null },
  rsvpOpen: true,
  rsvpClosesAt: closesInDays === null ? null : deadlineAt(now, closesInDays).toISOString(),
  links: { babyspaceUrl: BABYSPACE_URL, enquiriesEmail: AGM_ENQUIRIES_EMAIL },
  briefing: { ...BRIEFING },
  ...overrides,
});

/**
 * The member's starting state. Each one says what the screens should do with it, so a
 * wrong screen is legible from the console without reading the code. `body` builds
 * what GET returns from the clock; `answer` is the answer the member starts with.
 */
export const AGM_HARNESS_SCENARIOS = [
  {
    key: 'open',
    title: 'A · open, not answered',
    expected:
      'The question with Yes / No, "You can change this until …" under it, the briefing row and the contact row. No calendar pill, no Babyspace.',
    answer: null,
    body: now => eligibleBody(now),
  },
  {
    key: 'open_soon',
    title: 'A · open, deadline in 2 days',
    expected: 'As A, with the deadline now written with its weekday.',
    answer: null,
    body: now => eligibleBody(now, { closesInDays: 2 }),
  },
  {
    key: 'open_tonight',
    title: 'A · open, last day',
    expected: 'As A, and the line reads "You can change this until tonight, 11:59pm." in amber.',
    answer: null,
    body: now => eligibleBody(now, { closesInDays: 0 }),
  },
  {
    key: 'open_no_deadline',
    title: 'A · open, no deadline in the CMS',
    expected: 'As A, with no "You can change this until" line at all.',
    answer: null,
    body: now => eligibleBody(now, { closesInDays: null }),
  },
  {
    key: 'attending',
    title: 'B · attending',
    expected:
      '"Attending" with Change RSVP and its deadline, Add to calendar in the header, the briefing and Babyspace rows.',
    answer: true,
    body: now => eligibleBody(now),
  },
  {
    key: 'not_attending',
    title: 'B · not attending',
    expected: '"Not attending" (neutral, not red) and Change RSVP. No calendar pill, no Babyspace.',
    answer: false,
    body: now => eligibleBody(now),
  },
  {
    key: 'attending_tonight',
    title: 'B · attending, last day',
    expected: 'As B · attending, and the deadline reads "by tonight, 11:59pm" in amber.',
    answer: true,
    body: now => eligibleBody(now, { closesInDays: 0 }),
  },
  {
    key: 'attending_bare',
    title: 'B · attending, no briefing or Babyspace',
    expected: 'As B · attending, with no "Before the AGM" section at all.',
    answer: true,
    body: now =>
      eligibleBody(now, {
        briefing: null,
        links: { babyspaceUrl: null, enquiriesEmail: AGM_ENQUIRIES_EMAIL },
      }),
  },
  {
    key: 'closed_attending',
    title: 'C · closed, attending',
    expected:
      '"Attending" with "RSVP closed on …". The calendar pill and Babyspace stay. No Change RSVP.',
    answer: true,
    body: now => eligibleBody(now, { rsvpOpen: false, closesInDays: -1 }),
  },
  {
    key: 'closed_not_attending',
    title: 'C · closed, not attending',
    expected: '"Not attending" with "RSVP closed on …". No calendar, no Babyspace.',
    answer: false,
    body: now => eligibleBody(now, { rsvpOpen: false, closesInDays: -1 }),
  },
  {
    key: 'closed_none',
    title: 'C · closed, never answered',
    expected: '"You didn\'t RSVP" with "RSVP closed on …". No buttons.',
    answer: null,
    body: now => eligibleBody(now, { rsvpOpen: false, closesInDays: -1 }),
  },
  {
    key: 'ended',
    title: 'Ended',
    expected:
      '"This AGM has ended" with its date, a dimmed header, no calendar pill and no "Before the AGM". The Me page row is gone.',
    answer: true,
    body: now =>
      eligibleBody(now, {
        agmActive: false,
        rsvpOpen: false,
        eventDays: -3,
        closesInDays: -9,
      }),
  },
  {
    key: 'no_agm',
    title: 'D · no AGM set up',
    expected: '"No AGM to RSVP for yet" and the contact row.',
    answer: null,
    body: () => ({
      eligible: true,
      agmActive: false,
      event: null,
      rsvp: { attending: null },
      rsvpOpen: false,
      rsvpClosesAt: null,
      links: { babyspaceUrl: null, enquiriesEmail: AGM_ENQUIRIES_EMAIL },
      briefing: null,
    }),
  },
  {
    key: 'ineligible',
    title: 'E · not eligible',
    expected:
      '"RSVP is for eligible members" with no event details. The Me page still shows the AGM row, as it does for everyone.',
    answer: null,
    body: () => ({ eligible: false, agmActive: true }),
  },
  {
    key: 'load_failed',
    title: 'F · could not load',
    expected: '"We couldn\'t load the AGM" with Try again. Pull to refresh retries too.',
    answer: null,
    outcomes: { load: 'server_error' },
    body: now => eligibleBody(now),
  },
];

/** Canned answers that replace the fake's normal behaviour, per call type. */
export const AGM_HARNESS_OUTCOMES = {
  save: [
    { key: 'ok', label: 'Normal' },
    { key: 'closed', label: '409 closed' },
    { key: 'server_error', label: '500' },
    { key: 'network', label: 'No network' },
  ],
  load: [
    { key: 'ok', label: 'Normal' },
    { key: 'server_error', label: '500' },
    { key: 'network', label: 'No network' },
  ],
};

export const AGM_HARNESS_LATENCIES = [
  { ms: 0, label: 'None' },
  { ms: 1500, label: '1.5 s' },
  { ms: 4000, label: '4 s' },
];

// ── Persistence ─────────────────────────────────────────────────────────────

const defaultState = () => ({
  enabled: false,
  scenario: 'open',
  // The member's answer on the fake backend: null, true or false. A save replaces it.
  answer: null,
  // The fake's 409 closes RSVP for good, as ops flipping Status would.
  forcedClosed: false,
  outcomes: { save: 'ok', load: 'ok' },
  latencyMs: 0,
});

function readState() {
  try {
    const raw = window.localStorage.getItem(AGM_HARNESS_KEY);
    if (!raw) return defaultState();
    const saved = JSON.parse(raw);
    const base = defaultState();
    return { ...base, ...saved, outcomes: { ...base.outcomes, ...saved.outcomes } };
  } catch {
    return defaultState();
  }
}

function writeState(next) {
  try {
    window.localStorage.setItem(AGM_HARNESS_KEY, JSON.stringify(next));
  } catch {
    /* storage unavailable: the harness simply stays off */
  }
  return next;
}

/** True only when switched on AND not a production build. */
export function isAgmHarnessActive() {
  if (process.env.VUE_APP_NODE_ENV === 'production') return false;
  return readState().enabled === true;
}

export function getAgmHarnessState() {
  return readState();
}

export function setAgmHarness(patch) {
  return writeState({ ...readState(), ...patch });
}

const scenarioFor = key =>
  AGM_HARNESS_SCENARIOS.find(s => s.key === key) || AGM_HARNESS_SCENARIOS[0];

/** Switch the harness on with a scenario, replacing the answer and the forced close. */
export function applyAgmScenario(key) {
  const scenario = scenarioFor(key);
  const state = readState();
  return writeState({
    ...state,
    enabled: true,
    scenario: scenario.key,
    answer: scenario.answer,
    forcedClosed: false,
    // A scenario's own load outcome (F) must not outlive it; the save outcome is the
    // tester's choice and stays.
    outcomes: { ...state.outcomes, load: 'ok', ...scenario.outcomes },
  });
}

export function setAgmOutcome(kind, key) {
  const state = readState();
  return writeState({ ...state, outcomes: { ...state.outcomes, [kind]: key } });
}

export function resetAgmHarness() {
  return writeState(defaultState());
}

// ── The fake API ────────────────────────────────────────────────────────────

// Errors shaped like axios's, so the adapter reads real and fake failures alike.
const httpError = (status, data = {}) => {
  const error = new Error(`Request failed with status code ${status}`);
  error.response = { status, data };
  return error;
};

const networkError = () => {
  const error = new Error('Network Error');
  error.request = {};
  return error;
};

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

/** What GET /v2/agm answers for this state, at `now`. */
export function agmHarnessBody(state, now = new Date()) {
  const body = scenarioFor(state.scenario).body(now);
  if (!body.eligible) return body;
  const closesAt = body.rsvpClosesAt ? new Date(body.rsvpClosesAt) : null;
  const closed = state.forcedClosed || (closesAt !== null && now.getTime() >= closesAt.getTime());
  return {
    ...body,
    rsvp: { attending: state.answer ?? null },
    rsvpOpen: body.event ? body.rsvpOpen && !closed : false,
  };
}

async function respond(kind, run) {
  const state = readState();
  if (state.latencyMs) await sleep(state.latencyMs);
  const outcome = state.outcomes[kind];
  if (outcome === 'network') throw networkError();
  if (outcome === 'server_error') throw httpError(500, { message: 'Internal server error' });
  if (outcome === 'closed') {
    writeState({ ...readState(), forcedClosed: true });
    throw httpError(409, { code: 'RSVP_CLOSED' });
  }
  return run(state);
}

// Each returns what axios would: `{ data }`.
export const fakeAgmApi = {
  fetchAgm() {
    return respond('load', state => ({ data: agmHarnessBody(state) }));
  },

  // The API's rules (plan §4.2): 403 for a member who is not eligible, 404 with no
  // event, 409 once RSVP is closed; otherwise the answer is stored and the whole
  // screen comes back.
  putRsvp(attending) {
    return respond('save', state => {
      const body = agmHarnessBody(state);
      if (!body.eligible) throw httpError(403, { message: 'Forbidden' });
      if (!body.event) throw httpError(404, { message: 'Not found' });
      if (!body.rsvpOpen) throw httpError(409, { code: 'RSVP_CLOSED' });
      const next = writeState({ ...state, answer: attending });
      return { data: agmHarnessBody(next) };
    });
  },
};
